import { SearchResult } from '../types';

export interface SearchIndexItem {
  id: string;
  title: string;
  path: string;
  content: string;
  type: 'page' | 'section' | 'subsection';
  category: string;
  tags: string[];
  lastUpdated?: string;
  relevanceScore?: number;
}

export interface SearchFilters {
  category?: string;
  type?: 'page' | 'section' | 'subsection';
  dateRange?: 'all' | 'week' | 'month' | 'year';
}

export class SearchEngine {
  private searchIndex: SearchIndexItem[] = [];
  private isIndexed = false;

  // Build search index from available content
  buildIndex(): void {
    if (this.isIndexed) return;

    this.searchIndex = [];

    // Add main menu items as pages
    this.addMenuItemsToIndex();

    // Add component documentation
    this.addComponentDocumentation();

    // Add general documentation
    this.addGeneralDocumentation();

    this.isIndexed = true;
  }

  private addMenuItemsToIndex(): void {
    const menuItems = [
      { path: '/home/overview', title: 'Dashboard Overview', category: 'Home', content: 'View and manage your dashboard overview, customize widgets, and monitor key metrics.' },
      { path: '/home/custom-dashboards', title: 'Custom Dashboards', category: 'Home', content: 'Create and customize your own dashboards with widgets and charts.' },

      // Products
      { path: '/products/products', title: 'Products Management', category: 'Products', content: 'Add, edit, and manage your product catalog including SKUs, descriptions, and pricing.' },
      { path: '/products/product-groups', title: 'Product Groups', category: 'Products', content: 'Organize products into logical groups and categories for better management.' },
      { path: '/products/inventory-adjustments', title: 'Inventory Adjustments', category: 'Products', content: 'Adjust inventory levels, handle stock corrections, and manage inventory discrepancies.' },
      { path: '/products/transfer-orders', title: 'Transfer Orders', category: 'Products', content: 'Create and manage transfer orders between warehouses and locations.' },
      { path: '/products/product-categories', title: 'Product Categories', category: 'Products', content: 'Set up and manage product categories and classification systems.' },
      { path: '/products/product-units', title: 'Product Units', category: 'Products', content: 'Configure product units of measurement and conversion factors.' },
      { path: '/products/convert-product', title: 'Convert Product', category: 'Products', content: 'Convert products between different units and handle product transformations.' },

      // Purchases
      { path: '/purchases/suppliers', title: 'Suppliers Management', category: 'Purchases', content: 'Add, edit, and manage supplier information, contacts, and payment terms.' },
      { path: '/purchases/supplier-group', title: 'Supplier Groups', category: 'Purchases', content: 'Organize suppliers into logical groups for better categorization and reporting.' },
      { path: '/purchases/expenses', title: 'Expenses Management', category: 'Purchases', content: 'Track and categorize business expenses, attach receipts, and manage expense reports.' },
      { path: '/purchases/recurring-expenses', title: 'Recurring Expenses', category: 'Purchases', content: 'Set up and manage recurring expenses like subscriptions and regular payments.' },
      { path: '/purchases/purchase-orders', title: 'Purchase Orders', category: 'Purchases', content: 'Create, track, and manage purchase orders with suppliers.' },
      { path: '/purchases/bills', title: 'Bills Management', category: 'Purchases', content: 'Receive and process vendor bills, set up payment schedules, and track payables.' },
      { path: '/purchases/payments-made', title: 'Payments Made', category: 'Purchases', content: 'Track payments made to suppliers and vendors with detailed transaction history.' },
      { path: '/purchases/supplier-credits', title: 'Supplier Credits', category: 'Purchases', content: 'Handle supplier credits, returns, and adjustments to accounts payable.' },

      // Sales
      { path: '/sales/customers', title: 'Customers Management', category: 'Sales', content: 'Add, edit, and manage customer information, contacts, and preferences.' },
      { path: '/sales/customer-groups', title: 'Customer Groups', category: 'Sales', content: 'Organize customers into groups for targeted marketing and reporting.' },
      { path: '/sales/sales-orders', title: 'Sales Orders', category: 'Sales', content: 'Create and manage sales orders, track order status, and manage fulfillment.' },
      { path: '/sales/invoices', title: 'Invoices Management', category: 'Sales', content: 'Create, send, and track invoices, manage payment terms, and handle collections.' },
      { path: '/sales/payments-received', title: 'Payments Received', category: 'Sales', content: 'Track customer payments, apply payments to invoices, and manage accounts receivable.' },
      { path: '/sales/credit-notes', title: 'Credit Notes', category: 'Sales', content: 'Issue credit notes for returns, adjustments, and customer account corrections.' },
      { path: '/sales/retail-sales', title: 'Retail Sales', category: 'Sales', content: 'Process retail transactions, manage point of sale, and track daily sales.' },

      // Banking
      { path: '/banking', title: 'Banking & Finance', category: 'Banking', content: 'Manage bank accounts, reconcile transactions, and track financial activities.' },

      // Budget
      { path: '/budget', title: 'Budget Management', category: 'Budget', content: 'Create and track budgets, monitor spending, and analyze financial performance.' },

      // Accountant
      { path: '/accountant/chart-of-accounts', title: 'Chart of Accounts', category: 'Accountant', content: 'Set up and manage your chart of accounts for proper financial reporting.' },
      { path: '/accountant/manual-journals', title: 'Manual Journals', category: 'Accountant', content: 'Create manual journal entries for complex transactions and adjustments.' },
      { path: '/accountant/transaction-locking', title: 'Transaction Locking', category: 'Accountant', content: 'Lock transactions to prevent modifications during closing periods.' },

      // Reports
      { path: '/report', title: 'Reports & Analytics', category: 'Report', content: 'Generate reports, analyze data, and gain insights into your business performance.' },

      // Settings
      { path: '/settings/advanced-options', title: 'Advanced Options', category: 'Settings', content: 'Configure advanced system options and preferences.' },
      { path: '/settings/branches', title: 'Branches Management', category: 'Settings', content: 'Set up and manage multiple business locations and branches.' },
      { path: '/settings/currencies', title: 'Currencies Management', category: 'Settings', content: 'Configure multiple currencies and exchange rates for international business.' },
      { path: '/settings/DeliveryMethods', title: 'Delivery Methods', category: 'Settings', content: 'Set up shipping and delivery methods for customer orders.' },
      { path: '/settings/opening-balances', title: 'Opening Balances', category: 'Settings', content: 'Set initial account balances when starting with the system.' },
      { path: '/settings/PaymentMethods', title: 'Payment Methods', category: 'Settings', content: 'Configure accepted payment methods for customer transactions.' },
      { path: '/settings/PaymentModes', title: 'Payment Modes', category: 'Settings', content: 'Set up different payment modes and terms for various transaction types.' },
      { path: '/settings/PosPaymentModes', title: 'POS Payment Modes', category: 'Settings', content: 'Configure payment methods accepted at point of sale locations.' },
      { path: '/settings/ProductConversationLink', title: 'Product Conversion Link', category: 'Settings', content: 'Set up product conversion relationships and formulas.' },
      { path: '/settings/profile', title: 'Profile Settings', category: 'Settings', content: 'Manage your user profile, preferences, and account settings.' },
      { path: '/settings/reasons', title: 'Reasons Management', category: 'Settings', content: 'Configure reason codes for various business processes and transactions.' },
      { path: '/settings/roles', title: 'Roles & Permissions', category: 'Settings', content: 'Set up user roles and define access permissions for different system areas.' },
      { path: '/settings/SalesPersons', title: 'Sales Persons', category: 'Settings', content: 'Manage sales team members and their territories or assignments.' },
      { path: '/settings/taxes', title: 'Taxes Management', category: 'Settings', content: 'Configure tax rates, rules, and tax codes for different jurisdictions.' },
      { path: '/settings/TransactionNumberSeries', title: 'Transaction Number Series', category: 'Settings', content: 'Set up numbering sequences for various transaction types.' },
      { path: '/settings/users', title: 'Users Management', category: 'Settings', content: 'Add, edit, and manage system users and their access permissions.' },
      { path: '/settings/warehouses', title: 'Warehouses Management', category: 'Settings', content: 'Set up and manage warehouse locations and inventory storage areas.' },
      { path: '/settings/WorkCenter', title: 'Work Center', category: 'Settings', content: 'Configure work centers for manufacturing and production processes.' }
    ];

    menuItems.forEach(item => {
      this.searchIndex.push({
        id: item.path,
        title: item.title,
        path: item.path,
        content: item.content,
        type: 'page',
        category: item.category,
        tags: this.extractTags(item.title, item.content),
        lastUpdated: '2024-01-15'
      });
    });
  }

  private addComponentDocumentation(): void {
    const components = [
      {
        path: '/components/settings',
        title: 'Settings Components',
        category: 'Components',
        content: 'Reusable settings components including forms, tables, and configuration panels for various system settings.',
        type: 'section' as const
      },
      {
        path: '/components/product',
        title: 'Product Components',
        category: 'Components',
        content: 'Product management components for catalog, inventory, and product-related operations.',
        type: 'section' as const
      },
      {
        path: '/components/purchases',
        title: 'Purchase Components',
        category: 'Components',
        content: 'Purchase and procurement components for supplier management and purchase operations.',
        type: 'section' as const
      }
    ];

    components.forEach(component => {
      this.searchIndex.push({
        id: component.path,
        title: component.title,
        path: component.path,
        content: component.content,
        type: component.type,
        category: component.category,
        tags: this.extractTags(component.title, component.content),
        lastUpdated: '2024-01-15'
      });
    });
  }

  private addGeneralDocumentation(): void {
    const generalDocs = [
      {
        path: '/getting-started',
        title: 'Getting Started Guide',
        category: 'Documentation',
        content: 'Complete guide to getting started with DAS, including initial setup, configuration, and first steps.',
        type: 'page' as const
      },
      {
        path: '/user-manual',
        title: 'User Manual',
        category: 'Documentation',
        content: 'Comprehensive user manual covering all features and functionality of the DAS system.',
        type: 'page' as const
      },
      {
        path: '/api-documentation',
        title: 'API Documentation',
        category: 'Documentation',
        content: 'Technical documentation for integrating with DAS through APIs and webhooks.',
        type: 'page' as const
      }
    ];

    generalDocs.forEach(doc => {
      this.searchIndex.push({
        id: doc.path,
        title: doc.title,
        path: doc.path,
        content: doc.content,
        type: doc.type,
        category: doc.category,
        tags: this.extractTags(doc.title, doc.content),
        lastUpdated: '2024-01-15'
      });
    });
  }

  private extractTags(title: string, content: string): string[] {
    const text = `${title} ${content}`.toLowerCase();
    const commonTags = [
      'management', 'create', 'edit', 'delete', 'view', 'configure', 'setup',
      'reports', 'analytics', 'inventory', 'sales', 'purchases', 'accounting',
      'users', 'permissions', 'settings', 'dashboard', 'products', 'customers',
      'suppliers', 'invoices', 'payments', 'taxes', 'currencies', 'warehouses'
    ];

    return commonTags.filter(tag => text.includes(tag));
  }

  // Search functionality
  search(query: string, filters?: SearchFilters): SearchResult[] {
    if (!this.isIndexed) {
      this.buildIndex();
    }

    if (!query.trim()) {
      return [];
    }

    const queryLower = query.toLowerCase();
    const queryWords = queryLower.split(/\s+/).filter(word => word.length > 2);

    // Score and filter results
    const scoredResults = this.searchIndex
      .map(item => ({
        ...item,
        relevanceScore: this.calculateRelevanceScore(item, queryLower, queryWords)
      }))
      .filter(item => item.relevanceScore > 0)
      .filter(item => this.applyFilters(item, filters))
      .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));

    // Convert to SearchResult format
    return scoredResults.slice(0, 20).map(item => ({
      id: item.id,
      title: item.title,
      path: item.path,
      excerpt: this.generateExcerpt(item.content, queryWords),
      type: item.type
    }));
  }

  private calculateRelevanceScore(item: SearchIndexItem, query: string, queryWords: string[]): number {
    let score = 0;
    const titleLower = item.title.toLowerCase();
    const contentLower = item.content.toLowerCase();

    // Exact title match gets highest score
    if (titleLower === query) {
      score += 100;
    }
    // Title contains query
    else if (titleLower.includes(query)) {
      score += 50;
    }

    // Word matches in title
    queryWords.forEach(word => {
      if (titleLower.includes(word)) {
        score += 30;
      }
    });

    // Word matches in content
    queryWords.forEach(word => {
      const wordCount = (contentLower.match(new RegExp(word, 'g')) || []).length;
      score += wordCount * 5;
    });

    // Category relevance
    if (item.category.toLowerCase().includes(query)) {
      score += 20;
    }

    // Tag matches
    item.tags.forEach(tag => {
      if (tag.includes(query)) {
        score += 15;
      }
    });

    return score;
  }

  private applyFilters(item: SearchIndexItem, filters?: SearchFilters): boolean {
    if (!filters) return true;

    if (filters.category && item.category !== filters.category) {
      return false;
    }

    if (filters.type && item.type !== filters.type) {
      return false;
    }

    if (filters.dateRange && filters.dateRange !== 'all') {
      // Implement date filtering logic here
      // For now, return true
      return true;
    }

    return true;
  }

  private generateExcerpt(content: string, queryWords: string[]): string {
    if (content.length <= 150) {
      return content;
    }

    // Find the best position to start the excerpt
    let bestPosition = 0;
    let maxWordMatches = 0;

    for (let i = 0; i <= content.length - 150; i += 10) {
      const excerpt = content.substring(i, i + 150).toLowerCase();
      const wordMatches = queryWords.filter(word => excerpt.includes(word)).length;

      if (wordMatches > maxWordMatches) {
        maxWordMatches = wordMatches;
        bestPosition = i;
      }
    }

    let excerpt = content.substring(bestPosition, bestPosition + 150);

    // Try to start at a word boundary
    const firstSpace = excerpt.indexOf(' ');
    if (firstSpace > 0 && firstSpace < 30) {
      excerpt = excerpt.substring(firstSpace + 1);
    }

    // Add ellipsis if we're not at the end
    if (bestPosition + 150 < content.length) {
      excerpt += '...';
    }

    return excerpt;
  }

  // Get search suggestions
  getSuggestions(query: string): string[] {
    if (!this.isIndexed) {
      this.buildIndex();
    }

    if (!query.trim()) {
      return [];
    }

    const queryLower = query.toLowerCase();
    const suggestions = new Set<string>();

    this.searchIndex.forEach(item => {
      // Title suggestions
      if (item.title.toLowerCase().includes(queryLower)) {
        suggestions.add(item.title);
      }

      // Category suggestions
      if (item.category.toLowerCase().includes(queryLower)) {
        suggestions.add(item.category);
      }

      // Tag suggestions
      item.tags.forEach(tag => {
        if (tag.toLowerCase().includes(queryLower)) {
          suggestions.add(tag);
        }
      });
    });

    return Array.from(suggestions).slice(0, 8);
  }

  // Get available categories
  getCategories(): string[] {
    if (!this.isIndexed) {
      this.buildIndex();
    }

    const categories = new Set(this.searchIndex.map(item => item.category));
    return Array.from(categories).sort();
  }

  // Get popular searches
  getPopularSearches(): string[] {
    return [
      'invoice',
      'customer',
      'supplier',
      'inventory',
      'reports',
      'settings',
      'dashboard',
      'products',
      'payments',
      'taxes'
    ];
  }
}

// Export singleton instance
export const searchEngine = new SearchEngine();
